import { TableActionMenu } from '@assetworks-llc/aw-component-lib';

/** The Associated column's badge on the details-page association tables. */
export interface AssociationBadge {
  isAssociated: boolean;
  color: string;
  label: 'Yes' | 'No';
}

/** The Status column's badge. */
export interface StatusBadge {
  enabled: boolean;
  color: string;
  label: 'Active' | 'Disabled';
}

type AuthType = 0 | 1 | 2 | undefined;

/** A row of Third-Party Integration Details' Customers table. A type alias, so it stays assignable to CCL's TableRow. */
export type CustomerRow = {
  customerInfo: {
    customerName: string;
    customerId: string;
    customerIntegrationPartnerIDs: string[];
    detailsPageRoute: string;
  };
  associationBadge: AssociationBadge;
  badgeNested: StatusBadge;
  authType: AuthType;
};

/** A row of Third-Party Details' Third-Party Integrations table. */
export type IntegrationRow = {
  integrationInfo: {
    integrationName: string;
    integrationId: string;
    detailsPageRoute: string;
  };
  associationBadge: AssociationBadge;
  badgeNested: StatusBadge;
  authType: AuthType;
  customerIds: string[];
};

export function associationBadge(isAssociated: boolean): AssociationBadge {
  return {
    isAssociated,
    color: isAssociated ? 'success-green-700' : 'error-red',
    label: isAssociated ? 'Yes' : 'No',
  };
}

export function statusBadge(enabled: boolean): StatusBadge {
  return {
    enabled,
    color: enabled ? 'success-green-700' : 'aw-dark-gray-1000',
    label: enabled ? 'Active' : 'Disabled',
  };
}

/** What a row's … menu may offer and what each item does. The page supplies the permissions and the calls. */
export interface AssociationRowActionHandlers {
  canAssociate: boolean;
  canDisassociate: boolean;
  canSetStatus: boolean;
  associate: () => void;
  disassociate: () => void;
  setStatus: (enabled: boolean) => void;
}

/**
 * The row's … menu on both details-page association tables (Kohl, 2026-10-16): Associate or Disassociate, and
 * Set Active or Set Disabled, by the row's state and the user's permissions. Empty hides the row's menu.
 */
export function associationRowActions(
  row: { associationBadge: AssociationBadge; badgeNested: StatusBadge },
  handlers: AssociationRowActionHandlers
): TableActionMenu[] {
  const actions: TableActionMenu[] = [];
  const associated = row.associationBadge.isAssociated;
  if (!associated && handlers.canAssociate) {
    actions.push({ title: 'Associate', action: () => handlers.associate() });
  }
  if (associated && handlers.canDisassociate) {
    actions.push({ title: 'Disassociate', action: () => handlers.disassociate() });
  }
  if (handlers.canSetStatus) {
    const enabled = row.badgeNested.enabled;
    actions.push({
      title: enabled ? 'Set Disabled' : 'Set Active',
      action: () => handlers.setStatus(!enabled),
    });
  }
  return actions;
}
